import { PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";

/** Checkout / thank-you / order-status extension popups (compact widget). */
export const COMPACT_EXTENSION_PRODUCT_POPUP_TYPES = new Set([
  "checkout_extension_upsell_widget",
  "Checkout Extension",
  "thankyou_widget",
  "order_status_widget",
]);

const COMPACT_EXTENSION_EMBED_SURFACES = new Set([
  "checkout",
  "thank_you",
  "order_status",
]);

/** Surfaces that support list Layout 1 / Layout 2. */
export const LIST_LAYOUT_V2_POPUP_TYPES = COMPACT_EXTENSION_PRODUCT_POPUP_TYPES;

export const EXTENSION_PRODUCT_LAYOUT_OPTIONS = Object.freeze([
  {
    value: PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT,
    label: "Layout 1",
    description: "Image, title, and price; CTA beside quantity",
    preview: "cart-upsell-horizontal",
  },
  {
    value: PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2,
    label: "Layout 2",
    description: "CTA beside price; variant and quantity below",
    preview: "cart-upsell-horizontal",
  },
  {
    value: PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER,
    label: "Slider",
    description: "Horizontal scrollable product cards",
    preview: "cart-upsell-carousel",
  },
]);

/** @deprecated Use EXTENSION_PRODUCT_LAYOUT_OPTIONS. */
export const EXTENSION_THANKYOU_ORDER_LAYOUT_OPTIONS =
  EXTENSION_PRODUCT_LAYOUT_OPTIONS;

export function supportsExtensionListLayoutV2(popup) {
  return isCompactExtensionProductPopup(popup);
}

export function getExtensionProductLayoutOptions(_popup) {
  return EXTENSION_PRODUCT_LAYOUT_OPTIONS;
}

/** Fallback bounds when template/popup omits `condition.product` limit fields. */
export const EXTENSION_PRODUCT_OFFER_LIMIT_MIN = 1;
export const EXTENSION_PRODUCT_OFFER_LIMIT_MAX = 20;

/** Slider product card width bounds (px) — matches checkout extension carousel tiles. */
export const EXTENSION_SLIDER_TILE_MIN_PX = 155;
export const EXTENSION_SLIDER_TILE_MAX_PX = 340;
export const EXTENSION_SLIDER_TILE_DEFAULT_PX = 247;

/** Visible carousel cards per row in the extension slider. */
export const EXTENSION_SLIDER_ITEMS_PER_ROW_MIN = 1;
export const EXTENSION_SLIDER_ITEMS_PER_ROW_MAX = 8;
export const EXTENSION_SLIDER_ITEMS_PER_ROW_DEFAULT = 3;

function getExtensionGeneralSettings(popupOrGeneralSettings) {
  if (!popupOrGeneralSettings || typeof popupOrGeneralSettings !== "object") {
    return {};
  }
  return popupOrGeneralSettings.generalSettings || popupOrGeneralSettings;
}

function getExtensionTemplateProductCondition(popup) {
  if (!popup || typeof popup !== "object") return {};
  return (
    popup?.template_id?.default_schema?.generalSettings?.condition?.product ||
    popup?.template?.default_schema?.generalSettings?.condition?.product ||
    {}
  );
}

function readExtensionProductConditionBound(
  productCond,
  templateProductCond,
  key,
  fallback
) {
  const fromTemplate = Number(templateProductCond?.[key]);
  if (Number.isFinite(fromTemplate) && fromTemplate > 0) return fromTemplate;
  const fromPopup = Number(productCond?.[key]);
  if (Number.isFinite(fromPopup) && fromPopup > 0) return fromPopup;
  return fallback;
}

/** `products_to_offer_limit_min` / `_max` from popup or template, else static fallbacks. */
export function resolveExtensionOfferLimitBounds(popupOrGeneralSettings) {
  const popup =
    popupOrGeneralSettings?.generalSettings != null
      ? popupOrGeneralSettings
      : null;
  const generalSettings = getExtensionGeneralSettings(popupOrGeneralSettings);
  const productCond = generalSettings?.condition?.product || {};
  const templateProductCond = getExtensionTemplateProductCondition(popup);

  const min = readExtensionProductConditionBound(
    productCond,
    templateProductCond,
    "products_to_offer_limit_min",
    EXTENSION_PRODUCT_OFFER_LIMIT_MIN
  );
  const max = readExtensionProductConditionBound(
    productCond,
    templateProductCond,
    "products_to_offer_limit_max",
    EXTENSION_PRODUCT_OFFER_LIMIT_MAX
  );
  const safeMin = Math.max(1, min);
  return { min: safeMin, max: Math.max(safeMin, max) };
}

export function clampExtensionOfferLimit(value, popupOrGeneralSettings) {
  const { min, max } = resolveExtensionOfferLimitBounds(popupOrGeneralSettings);
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, Math.round(parsed)));
}

/** `products_per_row_limit` from popup or template caps slider items-per-row. */
export function resolveExtensionItemsPerRowBounds(popupOrGeneralSettings) {
  const popup =
    popupOrGeneralSettings?.generalSettings != null
      ? popupOrGeneralSettings
      : null;
  const generalSettings = getExtensionGeneralSettings(popupOrGeneralSettings);
  const productCond = generalSettings?.condition?.product || {};
  const templateProductCond = getExtensionTemplateProductCondition(popup);

  const max = readExtensionProductConditionBound(
    productCond,
    templateProductCond,
    "products_per_row_limit",
    EXTENSION_SLIDER_ITEMS_PER_ROW_MAX
  );

  return {
    min: EXTENSION_SLIDER_ITEMS_PER_ROW_MIN,
    max: Math.max(EXTENSION_SLIDER_ITEMS_PER_ROW_MIN, max),
    default: EXTENSION_SLIDER_ITEMS_PER_ROW_DEFAULT,
  };
}

/** Gap between slider cards (matches checkout `spacing="base"`). */
export const EXTENSION_SLIDER_ROW_GAP_PX = 14;

/** Horizontal scroll padding inside the slider track. */
export const EXTENSION_SLIDER_SCROLL_PADDING_PX = 18;

/** Approximate checkout block widths used for responsive tile sizing. */
export const EXTENSION_SLIDER_VIEWPORT_WIDTH = Object.freeze({
  small: 360,
  medium: 473,
  large: 538,
});

function resolveExtensionSliderItemsPerRowBounds(popupOrGeneralSettingsOrFallback) {
  if (
    popupOrGeneralSettingsOrFallback &&
    typeof popupOrGeneralSettingsOrFallback === "object"
  ) {
    return resolveExtensionItemsPerRowBounds(popupOrGeneralSettingsOrFallback);
  }

  if (Number.isFinite(Number(popupOrGeneralSettingsOrFallback))) {
    return {
      min: EXTENSION_SLIDER_ITEMS_PER_ROW_MIN,
      max: EXTENSION_SLIDER_ITEMS_PER_ROW_MAX,
      default: Number(popupOrGeneralSettingsOrFallback),
    };
  }

  return resolveExtensionItemsPerRowBounds(null);
}

export function clampExtensionSliderItemsPerRow(
  value,
  popupOrGeneralSettingsOrFallback = null
) {
  const bounds = resolveExtensionSliderItemsPerRowBounds(
    popupOrGeneralSettingsOrFallback
  );
  const raw = Number(value);
  const fallback = bounds.default ?? EXTENSION_SLIDER_ITEMS_PER_ROW_DEFAULT;
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(bounds.max, Math.max(bounds.min, Math.round(raw)));
}

export function resolveExtensionSliderItemsPerRow(
  productSection,
  popupOrGeneralSettings = null
) {
  return clampExtensionSliderItemsPerRow(
    productSection?.setting?.layout?.itemsPerRow,
    popupOrGeneralSettings
  );
}

/**
 * Resolve slider card width for a container, clamped between 155–340px.
 * Matches Reconvert BlocksGridLayout: always divide by requested items-per-row,
 * clamp tile width, and allow horizontal scroll when tiles cannot shrink below 155px.
 */
export function resolveExtensionSliderTileLayout({
  containerWidth = EXTENSION_SLIDER_VIEWPORT_WIDTH.large,
  itemsPerRow = EXTENSION_SLIDER_ITEMS_PER_ROW_DEFAULT,
  gap = EXTENSION_SLIDER_ROW_GAP_PX,
  scrollPadding = EXTENSION_SLIDER_SCROLL_PADDING_PX,
  popupOrGeneralSettings = null,
} = {}) {
  const requested = clampExtensionSliderItemsPerRow(
    itemsPerRow,
    popupOrGeneralSettings
  );
  const numericWidth = Number(containerWidth);
  const safeWidth =
    Number.isFinite(numericWidth) && numericWidth > 0
      ? numericWidth
      : EXTENSION_SLIDER_VIEWPORT_WIDTH.large;

  const gutterTotal = gap * Math.max(requested - 1, 0) + scrollPadding;
  const usableWidth = safeWidth - gutterTotal;
  let tileWidth = requested > 0 ? usableWidth / requested : usableWidth;

  tileWidth = Math.min(
    EXTENSION_SLIDER_TILE_MAX_PX,
    Math.max(EXTENSION_SLIDER_TILE_MIN_PX, tileWidth)
  );

  const visibleItemsPerRow = Math.max(
    1,
    Math.min(
      requested,
      Math.floor((safeWidth - scrollPadding + gap) / (tileWidth + gap))
    )
  );

  return {
    itemsPerRow: requested,
    visibleItemsPerRow,
    tileWidth: Math.round(tileWidth),
  };
}

export function resolveExtensionSliderTileLayoutsByViewport(
  itemsPerRow,
  popupOrGeneralSettings = null
) {
  return {
    small: resolveExtensionSliderTileLayout({
      containerWidth: EXTENSION_SLIDER_VIEWPORT_WIDTH.small,
      itemsPerRow,
      popupOrGeneralSettings,
    }),
    medium: resolveExtensionSliderTileLayout({
      containerWidth: EXTENSION_SLIDER_VIEWPORT_WIDTH.medium,
      itemsPerRow,
      popupOrGeneralSettings,
    }),
    large: resolveExtensionSliderTileLayout({
      containerWidth: EXTENSION_SLIDER_VIEWPORT_WIDTH.large,
      itemsPerRow,
      popupOrGeneralSettings,
    }),
  };
}

function getCompactExtensionProductLookupText(popup) {
  return [
    popup?.popUpType,
    popup?.template_type,
    popup?.title,
    popup?.generalSettings?.title,
    popup?.template_id?.title,
    popup?.template_id?.template_type,
    popup?.category?.primary,
    popup?.category?.key,
    popup?.cover_image,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isExcludedCompactExtensionText(text) {
  return (
    text.includes("post purchase") ||
    text.includes("post-purchase") ||
    text.includes("post_purchase") ||
    text.includes("cart drawer") ||
    text.includes("cart_drawer") ||
    text.includes("product page upsell") ||
    text.includes("product-page-upsell")
  );
}

function matchesCompactExtensionText(text) {
  if (!text || isExcludedCompactExtensionText(text)) return false;

  if (text.includes("order status")) return true;
  if (text.includes("thank you") || text.includes("thankyou") || text.includes("thank-you")) {
    return true;
  }
  if (text.includes("thank")) return true;
  if (
    text.includes("checkout product offer") ||
    text.includes("checkout extension") ||
    text.includes("checkout-extensions") ||
    text.includes("checkout_extension_upsell_widget")
  ) {
    return true;
  }

  return false;
}

export function isCompactExtensionProductPopup(popup) {
  if (!popup || typeof popup !== "object") return false;

  const embedSurface = String(
    popup?.generalSettings?.condition?.embed?.extension_surface || ""
  ).trim();
  if (COMPACT_EXTENSION_EMBED_SURFACES.has(embedSurface)) return true;

  const popUpType = String(popup.popUpType || popup.template_type || "");
  if (COMPACT_EXTENSION_PRODUCT_POPUP_TYPES.has(popUpType)) return true;

  return matchesCompactExtensionText(getCompactExtensionProductLookupText(popup));
}

export function isExtensionSliderShell(shell) {
  return shell === PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER;
}

export function isExtensionListV2Shell(shell) {
  return shell === PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2;
}

export function isExtensionListShell(shell) {
  return (
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT ||
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2 ||
    !shell ||
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_TWO_COLUMN
  );
}

function normalizeResolvedExtensionShell(shell, popup) {
  if (shell === PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER) {
    return PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER;
  }
  if (shell === PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2) {
    // Keep Layout 2 once saved; compact-only surfaces still render via list card.
    return PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2;
  }
  if (shell === PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT) {
    return PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT;
  }
  return null;
}

export function resolveExtensionProductShell(popup, productSection) {
  const fromProduct = normalizeResolvedExtensionShell(
    productSection?.setting?.layout?.shell,
    popup
  );
  if (fromProduct) return fromProduct;

  const fromGs = normalizeResolvedExtensionShell(
    popup?.generalSettings?.productShellLayout,
    popup
  );
  if (fromGs) return fromGs;

  return PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT;
}

export function buildExtensionProductLayoutFields(
  shell,
  existingLayout = {},
  popupOrGeneralSettings = null
) {
  const isSlider = isExtensionSliderShell(shell);
  const isListV2 = isExtensionListV2Shell(shell);
  const resolvedShell = isSlider
    ? PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER
    : isListV2
      ? PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2
      : PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT;
  const nextLayout = {
    ...existingLayout,
    shell: resolvedShell,
    imagePosition: isSlider ? "top" : "left",
    density: isSlider ? "compact" : "standard",
  };

  if (isSlider) {
    nextLayout.itemsPerRow = clampExtensionSliderItemsPerRow(
      existingLayout?.itemsPerRow,
      popupOrGeneralSettings
    );
  } else if ("itemsPerRow" in nextLayout) {
    delete nextLayout.itemsPerRow;
  }

  return nextLayout;
}

/** Slider is always available (including a single product card). */
export function extensionLayoutAvailableForOfferCount(_offerCount) {
  return true;
}
